<template>
	<div class="content-banner" :style="background">
		<DefaultIcon
			v-if="logo"
			:logo="logo"
			class="content-banner__logo"
		></DefaultIcon>

		<span v-if="title" class="content-banner__header">
			<SvgIcon v-if="icon" :icon="icon" class="content-banner__icon" />
			<h3 class="content-banner__title">
				{{ title }}
			</h3>
		</span>
		<p v-if="text" class="content-banner__text">
			{{ text }}
		</p>
		<DefaultButton
			v-if="ctaLink?.url || ctaModal"
			:cta-link="ctaLink"
			:modal="ctaModal"
			:icon="ctaIcon"
			:color="ctaColor"
			:gtm="ctaGtm"
			:disabled="ctaDisabled"
			:aria-label="ctaAriaLabel"
		/>
	</div>
</template>

<script setup>
const img = useImage();
const { replaceImageSrc } = useImageHelpers();

const props = defineProps({
	fields: { type: Object, required: true },
	rendering: { type: Object, required: false },
});

const logo = computed(() =>
	props.fields?.logo?.value?.src ? props.fields.logo.value : null
);
const title = computed(() => props.fields?.title?.value);
const icon = computed(() => props.fields?.icon?.value);
const text = computed(() => props.fields?.text?.value);
const backgroundColor = computed(() => props.fields?.backgroundColor?.value);
const backgroundImage = computed(() => props.fields?.backgroundImage?.value);
const ctaLink = computed(() => props.fields?.ctaLink?.value);
const ctaIcon = computed(() => props.fields?.ctaIcon?.value);
const ctaColor = computed(() => props.fields?.ctaColor?.value);
const ctaGtm = computed(() => props.fields?.ctaGtm?.value);
const ctaDisabled = computed(() => props.fields?.ctaDisabled?.value);
const ctaAriaLabel = computed(() => props.fields?.ctaAriaLabel?.value);
const ctaModal = computed(() => props.fields?.ctaModal);

const bgImgSrc = computed(() => {
	if (backgroundImage.value?.src) {
		return img(replaceImageSrc(backgroundImage.value.src), { quality: 90 });
	}
	return null;
});

const background = bgImgSrc.value
	? `background-image: url(${bgImgSrc.value});`
	: `background: ${backgroundColor.value};`;
</script>

<style lang="scss">
.content-banner {
	background-size: cover;
	background-repeat: no-repeat;
	background-position: top center;
	border-radius: 16px;
	padding: var(--spacing-3, 24px) var(--spacing-2, 16px);
	margin: var(--spacing-2, 16px);
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	gap: var(--spacing-3, 24px);

	.content-banner__logo,
	.content-banner__logo img {
		max-width: 200px;
		height: auto;
		width: 100%;
	}

	.picture-img {
		img {
			padding: 0px;
		}
	}

	.content-banner__text {
		text-align: center;
		font-size: var(--text-base, 16px);
		font-weight: 400;
		line-height: 140%;
		margin: 0px;
	}

	.content-banner__header {
		display: flex;
		align-items: center;
		gap: var(--spacing-1, 8px);

		.content-banner__icon {
			width: 40px;
			height: 40px;
			display: flex;
		}

		.content-banner__title {
			text-align: center;
			font-size: var(--text-xl, 24px);
			line-height: 140%;
			font-style: normal;
			font-weight: 900;
			line-height: 120%;
			text-transform: uppercase;
			margin: 0px;
		}
	}

	.cta-btn {
		display: flex;
		justify-content: center;
		align-items: center;
	}
}
</style>
